import { useState } from 'react';
import { QuestionTitleInput } from './QuestionTitleInput';
import { QuestionTypeSelector } from './QuestionTypeSelector';
import { ChevronDown, ChevronUp, Settings, Trash } from 'lucide-react';
import { QuestionOptionsEditor } from './QuestionOptionsEditor';
import { cn } from '@client/lib';
import { ConditionalBuilder } from './ConditionalBuilder';
import { TableColumnEditor } from './TableColumnEditor';

export function QuestionRow({
  q,
  subsectionId,
  sectionId,
  groupId,
  onQuestionChange,
  onRemoveQuestion,
  ...props
}: {
  q: PolicyFormQuestion;
  subsectionId: string;
  sectionId: string;
  groupId: string;
  questions: Record<string, PolicyFormQuestion>;
  onQuestionChange: (id: string, updates: Partial<PolicyFormQuestion>) => void;
  onRemoveQuestion: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    questionId: string,
  ) => void;
}) {
  const [showOptions, setShowOptions] = useState(false);
  const [showTableColumns, setShowTableColumns] = useState(false);
  const [required, setRequired] = useState(q.required);
  const [isConditionOpen, setIsConditionOpen] = useState(false);

  const onToggleRequired = () => {
    setRequired(!required);
    onQuestionChange(q.questionRef, { required: !required });
  };

  return (
    <div className="flex flex-col border-b px-1 py-2 hover:bg-gray-50">
      {/* Row Header */}
      <div className="flex items-center gap-3">
        <QuestionTitleInput
          questionId={q.questionRef}
          value={q.title}
          onChange={onQuestionChange}
        />

        <span className="font-mono text-xs text-gray-500">{q.questionRef}</span>

        <QuestionTypeSelector
          questionId={q.questionRef}
          value={q.type}
          onChange={onQuestionChange}
        />

        {(q.type === 'select' || q.type === 'multiselect' || q.type === 'radio') && (
          <button
            className="mr-2 flex items-center gap-1 text-xs text-blue-600"
            onClick={() => setShowOptions(prev => !prev)}
          >
            {showOptions ? (
              <>
                <ChevronUp size={14} /> Hide Options
              </>
            ) : (
              <>
                <ChevronDown size={14} /> View Options
              </>
            )}
          </button>
        )}
        {q.type === 'table' && (
          <button
            className="mr-2 flex items-center gap-1 text-xs text-blue-600"
            onClick={() => setShowTableColumns(prev => !prev)}
          >
            {showTableColumns ? (
              <>
                <ChevronUp size={14} /> Hide Table Columns
              </>
            ) : (
              <>
                <ChevronDown size={14} /> View Table Columns
              </>
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onToggleRequired}
          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors
        ${required ? 'bg-red-500 text-white' : 'border border-red-500 bg-white text-red-700'}`}
          title={required ? 'Required' : 'Not Required'}
        >
          R
        </button>

        <button
          type="button"
          onClick={() => {
            setIsConditionOpen(true);
          }}
        >
          <Settings
            className={cn(
              'h-4 w-4 flex-shrink-0',
              q.conditional ? 'text-green-500' : 'text-gray-700 ',
            )}
          />
        </button>

        <ConditionalBuilder
          open={isConditionOpen}
          title={`Conditional Logic - Questions`}
          visibleCondition={q?.conditional?.visible}
          requiredCondition={q?.conditional?.required}
          onChangeVisible={c =>
            onQuestionChange(q.questionRef, {
              conditional: { ...(q.conditional ?? {}), visible: c },
            })
          }
          onChangeRequired={c =>
            onQuestionChange(q.questionRef, {
              conditional: { ...(q.conditional ?? {}), required: c },
            })
          }
          onClose={() => {
            setIsConditionOpen(false);
          }}
          questions={props.questions}
        />

        <button
          type="button"
          onClick={() => onRemoveQuestion(sectionId, subsectionId, groupId, q.questionRef)}
          className=""
        >
          <Trash className="h-4 w-4 flex-shrink-0 text-red-700" />
        </button>
      </div>

      {showOptions && (
        <QuestionOptionsEditor
          q={q}
          onQuestionChange={onQuestionChange}
        />
      )}
      {showTableColumns && q.type === 'table' && (
        <div>
          {q.columns?.map(col => (
            <TableColumnEditor
              key={col.id}
              column={col}
              onChange={updatedCol => {
                const updatedColumns = q.columns?.map(c =>
                  c.id === updatedCol.id ? updatedCol : c,
                );
                onQuestionChange(q.questionRef, { columns: updatedColumns });
              }}
              onRemove={() => {
                const updatedColumns = q.columns?.filter(c => c.id !== col.id);
                onQuestionChange(q.questionRef, { columns: updatedColumns });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
